import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import './ReviewCard.css';
import PersonIcon from '@mui/icons-material/Person';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { axiosInstance } from '../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';

const ReviewCard = ({ review, onReplySuccess, onHideSuccess, instructorProfile }) => {
  const [replyMode, setReplyMode] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [hasReplied, setHasReplied] = useState(!!review.teacher_response);
  const [instructorReply, setInstructorReply] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isHidden, setIsHidden] = useState(review.is_hidden ?? false);
  const [hideToggling, setHideToggling] = useState(false);
  const getSessionTypeColor = type => {
    const typeMap = {
      'One-On-One': '#6B60E7',
      'Group Session': '#10B981',
      Prime: '#ECB100',
    };
    return typeMap[type] || '#6B60E7';
  };

  const getSchoolLevelColor = level => {
    const levelMap = {
      'High School': '#E0AD14',
      'Middle School': '#3B82F6',
      'Elementary School': '#8B5CF6',
      College: '#EF4444',
      University: '#F59E0B',
    };
    return levelMap[level] || '#E0AD14';
  };

  useEffect(() => {
    setIsHidden(review.is_hidden ?? false);
  }, [review.is_hidden]);

  useEffect(() => {
    if (review.teacher_response && instructorProfile) {
      setInstructorReply({
        name: instructorProfile.name,
        image: instructorProfile.image,
        date: review.responded_at
          ? format(parseISO(review.responded_at), 'd MMM, yyyy')
          : format(new Date(), 'd MMM, yyyy'),
        text: review.teacher_response,
      });
    }
  }, [review, instructorProfile]);

  const handleHideToggle = async event => {
    // Switch checked=true means visible, checked=false means hidden
    const newHidden = !event.target.checked;
    const action = newHidden ? 'hide' : 'unhide';
    setHideToggling(true);
    try {
      await axiosInstance.patch(`/teachers/reviews/${review.id}/hide`, null, {
        params: { action },
      });
      setIsHidden(newHidden);
      toast.success(newHidden ? 'Review hidden' : 'Review visible');
      if (onHideSuccess) onHideSuccess(review.id, newHidden);
    } catch (error) {
      console.error('Error toggling review visibility:', error);
      toast.error('Failed to update review visibility. Please try again.');
    } finally {
      setHideToggling(false);
    }
  };

  const handleReplyClick = () => {
    setReplyMode(true);
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || submitting) return;

    setSubmitting(true);
    try {
      const response = await axiosInstance.patch(`/teachers/reviews/${review.id}/respond`, {
        response: replyText.trim(),
      });

      setInstructorReply({
        name: instructorProfile?.name || 'Instructor',
        image: instructorProfile?.image || '',
        date: response.data.responded_at
          ? format(parseISO(response.data.responded_at), 'd MMM, yyyy')
          : format(new Date(), 'd MMM, yyyy'),
        text: response.data.teacher_response,
      });
      setHasReplied(true);
      setReplyMode(false);
      setReplyText('');
      toast.success('Reply sent successfully!');

      if (onReplySuccess) {
        onReplySuccess(review.id);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const renderStars = rating => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} style={{ color: i < rating ? '#FFC107' : '#E0E0E0' }}>
          ⭐
        </span>
      );
    }
    return stars;
  };

  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box className="review-card">
      <Box className="review-card-header" sx={{ position: 'relative' }}>
        {review.learner.profile_image ? (
          <img
            src={review.learner.profile_image}
            alt={review.learner.name}
            className="review-learner-image"
          />
        ) : (
          <Box
            className="review-learner-image"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#E0E0E0',
            }}
          >
            <PersonIcon sx={{ fontSize: 30, color: '#757575' }} />
          </Box>
        )}
        <Box className="review-learner-info">
          <Box className="review-learner-name-section">
            <Typography className="review-learner-name">{review.learner.name}</Typography>
            <Box className="review-session-type-badge">
              <PersonIcon
                sx={{
                  fontSize: 16,
                  color: getSessionTypeColor(review.session.type),
                }}
              />
              <Typography
                component="span"
                sx={{
                  color: `${getSessionTypeColor(review.session.type)} !important`,
                }}
              >
                {review.session.type}
              </Typography>
            </Box>
          </Box>
          <Typography
            className="review-school-level"
            sx={{ color: `${getSchoolLevelColor(review.learner.school_level)} !important` }}
          >
            {review.learner.school_level}
          </Typography>
        </Box>
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={!isHidden}
                onChange={handleHideToggle}
                disabled={hideToggling}
                size="small"
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isHidden ? (
                  <VisibilityOffIcon sx={{ fontSize: 16, color: '#949da6' }} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 16, color: '#0a5ef3' }} />
                )}
                <Typography sx={{ fontSize: 12, color: '#56585e' }}>
                  {isHidden ? 'Hidden' : 'Visible'}
                </Typography>
              </Box>
            }
            sx={{ margin: 0 }}
          />
        </Box>
      </Box>

      <Typography className="review-session-detail-title">Session Detail</Typography>

      <Box className="review-session-details-grid">
        <Box className="review-detail-item">
          <Typography className="review-detail-label">Subject</Typography>
          <Typography className="review-detail-value">{review.session.subject}</Typography>
        </Box>
        <Box className="review-detail-item">
          <Typography className="review-detail-label">Date</Typography>
          <Typography className="review-detail-value">{review.session.date}</Typography>
        </Box>
        <Box className="review-detail-item">
          <Typography className="review-detail-label">Timing</Typography>
          <Typography className="review-detail-value">{review.session.timing}</Typography>
        </Box>
        <Box className="review-detail-item">
          <Typography className="review-detail-label">Session Type</Typography>
          <Typography className="review-detail-value">{review.session.type}</Typography>
        </Box>
      </Box>

      <Typography className="review-text">{review.review_text}</Typography>

      <Box className="review-rating-section">
        <Box className="review-stars">
          {renderStars(review.rating)}
          <Typography className="review-rating-text">{review.rating}/5</Typography>
        </Box>

        {!replyMode && !hasReplied && (
          <button className="review-reply-button" onClick={handleReplyClick}>
            Reply
          </button>
        )}

        {replyMode && (
          <Box className="reply-input-container">
            <TextField
              fullWidth
              placeholder="Type your reply..."
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply();
                }
              }}
              variant="outlined"
              size="small"
              className="reply-input-field"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={handleSendReply}
                    className="reply-send-button"
                    disabled={!replyText.trim() || submitting}
                  >
                    {submitting ? (
                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 16, color: '#fff' }} />
                    )}
                  </IconButton>
                ),
              }}
            />
          </Box>
        )}

        {hasReplied && instructorReply && (
          <Box className="instructor-response-container">
            <Box className="instructor-response-wrapper">
              <Box className="instructor-response-line" />
              <Box className="instructor-response-content">
                <Box className="instructor-response-header">
                  {instructorReply.image ? (
                    <img
                      src={instructorReply.image}
                      alt={instructorReply.name}
                      className="instructor-avatar"
                    />
                  ) : (
                    <Box
                      className="instructor-avatar"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#E0E0E0',
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 12, color: '#757575' }} />
                    </Box>
                  )}
                  <Typography className="instructor-name">{instructorReply.name}</Typography>
                </Box>
                <Box className="instructor-response-meta">
                  <Typography className="instructor-response-label">Instructor response</Typography>
                  <span className="instructor-response-dot">•</span>
                  <Typography className="instructor-response-date">
                    {instructorReply.date}
                  </Typography>
                </Box>
                <Box className="instructor-response-text-container">
                  <Typography className="instructor-response-text">
                    {isExpanded ? instructorReply.text : truncateText(instructorReply.text)}
                  </Typography>
                  {instructorReply.text.length > 150 && (
                    <Typography className="instructor-response-toggle" onClick={toggleExpanded}>
                      {isExpanded ? 'Less' : 'More'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReviewCard;
